import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { PackagingApiAdapter } from '../../materialAlmacen/adapters/PackagingApiAdapter';
import { PackagingRulesetsApiAdapter } from '../adapters/PackagingRulesetsApiAdapter';
import {
  CreateBoxExceptionConditionPayload,
  CreateBoxExceptionPayload,
  CreateMarketingRuleActionPayload,
  CreateMarketingRuleConditionPayload,
} from '../models/PackagingRulesets';

const httpClient = createHttpClient(apiClientWH);
const apiAdapter = new PackagingRulesetsApiAdapter(httpClient);
/** Misma fuente que `/packaging/warehouse-material` (selector de grupos sin `team_id`). */
const packagingMaterialAdapter = new PackagingApiAdapter(httpClient);

/** Scope opcional: equipo hijo cuando un 3PL gestiona reglas desde `/clients/:id/packaging-rulesets`. */
export const usePackagingRulesets = (scopedTeamId?: number | null) => {
  const queryClient = useQueryClient();
  const teamId = scopedTeamId ?? undefined;
  const scopeKey = teamId ?? 'current';

  const boxRulesetsQuery = useQuery({
    queryKey: ['packaging-box-rulesets', scopeKey],
    queryFn: () => apiAdapter.getBoxRulesets(undefined, teamId),
    staleTime: 5 * 60 * 1000,
  });

  const marketingRulesetsQuery = useQuery({
    queryKey: ['packaging-marketing-rulesets', scopeKey],
    queryFn: () => apiAdapter.getMarketingRulesets(teamId),
    staleTime: 5 * 60 * 1000,
  });

  /** En vista 3PL por cliente: además de los grupos del tenant hijo, incluir los del almacén padre (como en warehouse-material). */
  const isScopedChildTeam = scopedTeamId != null;

  const allBoxGroupsQuery = useQuery({
    queryKey: ['packaging-box-groups', scopeKey, 'customer_box'],
    queryFn: () => apiAdapter.getAllBoxGroups(teamId, 'customer_box'),
    staleTime: 5 * 60 * 1000,
  });

  const parentWarehouseBoxGroupsQuery = useQuery({
    queryKey: ['packaging-box-groups', scopeKey, 'parent-warehouse', 'own_box'],
    queryFn: () => packagingMaterialAdapter.getBoxGroups(undefined, 'own_box'),
    staleTime: 5 * 60 * 1000,
    enabled: isScopedChildTeam,
  });

  const marketingItemsQuery = useQuery({
    queryKey: ['packaging-marketing-items', scopeKey],
    queryFn: () => apiAdapter.getMarketingPackagingItems(true, teamId),
    staleTime: 5 * 60 * 1000,
  });

  const countriesQuery = useQuery({
    queryKey: ['packaging-countries'],
    queryFn: () => apiAdapter.getCountries(),
    staleTime: 60 * 60 * 1000,
  });

  const activeBoxRulesetId = boxRulesetsQuery.data?.[0]?.id ?? null;

  const boxRulesetDetailQuery = useQuery({
    queryKey: ['packaging-box-ruleset-detail', scopeKey, activeBoxRulesetId],
    queryFn: () => apiAdapter.getBoxRulesetById(activeBoxRulesetId as number),
    enabled: Boolean(activeBoxRulesetId),
    staleTime: 5 * 60 * 1000,
  });

  const boxExceptionsQuery = useQuery({
    queryKey: ['packaging-box-exceptions', scopeKey, activeBoxRulesetId],
    queryFn: () => apiAdapter.getBoxRulesetExceptions(activeBoxRulesetId as number),
    enabled: Boolean(activeBoxRulesetId),
    staleTime: 2 * 60 * 1000,
  });

  const createMarketingRulesetMutation = useMutation({
    mutationFn: () => apiAdapter.createMarketingRuleset({ active: true }, teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packaging-marketing-rulesets'] });
    },
  });

  const createMarketingRuleMutation = useMutation({
    mutationFn: async (input: {
      rulesetId: number;
      name: string;
      priority: number;
      active: boolean;
      isAlways: boolean;
      conditions?: CreateMarketingRuleConditionPayload[];
      actions: CreateMarketingRuleActionPayload[];
    }) => {
      const created = await apiAdapter.createMarketingRule(input.rulesetId, {
        name: input.name,
        priority: input.priority,
        active: input.active,
      });

      if (!input.isAlways && input.conditions?.length) {
        await Promise.all(
          input.conditions.map((condition) => apiAdapter.createMarketingRuleCondition(created.id, condition)),
        );
      }

      await Promise.all(input.actions.map((action) => apiAdapter.createMarketingRuleAction(created.id, action)));

      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packaging-marketing-rulesets'] });
    },
  });

  const updateMarketingRuleMutation = useMutation({
    mutationFn: (input: { ruleId: number; name: string; priority: number; active: boolean }) =>
      apiAdapter.updateMarketingRule(input.ruleId, {
        name: input.name,
        priority: input.priority,
        active: input.active,
      }),
  });

  const deleteMarketingRuleMutation = useMutation({
    mutationFn: (ruleId: number) => apiAdapter.deleteMarketingRule(ruleId),
  });

  const createExceptionMutation = useMutation({
    mutationFn: async (input: {
      rulesetId: number;
      exception: CreateBoxExceptionPayload;
      conditions?: CreateBoxExceptionConditionPayload[];
    }) => {
      const created = await apiAdapter.createBoxException(input.rulesetId, input.exception);

      if (input.conditions?.length) {
        await Promise.all(
          input.conditions.map((condition) => apiAdapter.createBoxExceptionCondition(created.id, condition)),
        );
      }

      return created;
    },
    onSuccess: () => {
      // Debe coincidir con queryKey: ['packaging-box-exceptions', scopeKey, activeBoxRulesetId]
      queryClient.invalidateQueries({ queryKey: ['packaging-box-exceptions', scopeKey, activeBoxRulesetId] });
    },
  });

  const updateBoxExceptionMutation = useMutation({
    mutationFn: (input: {
      exceptionId: number;
      name: string;
      packaging_item_id: number;
      priority: number;
      active: boolean;
    }) =>
      apiAdapter.updateBoxException(input.exceptionId, {
        name: input.name,
        packaging_item_id: input.packaging_item_id,
        priority: input.priority,
        active: input.active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packaging-box-exceptions', scopeKey, activeBoxRulesetId] });
    },
  });

  const deleteBoxExceptionMutation = useMutation({
    mutationFn: (exceptionId: number) => apiAdapter.deleteBoxException(exceptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packaging-box-exceptions', scopeKey, activeBoxRulesetId] });
    },
  });

  const updateBoxRulesetMutation = useMutation({
    mutationFn: (input: {
      rulesetId: number;
      default_box_group_id: number | null;
      fallback_box_group_id?: number | null;
    }) =>
      apiAdapter.updateBoxRuleset(input.rulesetId, {
        active: true,
        default_box_group_id: input.default_box_group_id,
        fallback_box_group_id: input.fallback_box_group_id,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['packaging-box-ruleset-detail', variables.rulesetId] });
      queryClient.invalidateQueries({ queryKey: ['packaging-box-rulesets'] });
    },
  });

  const createBoxRulesetMutation = useMutation({
    mutationFn: (payload: {
      active: boolean;
      default_box_group_id?: number | null;
      fallback_box_group_id?: number | null;
    }) => apiAdapter.createBoxRuleset(payload, teamId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['packaging-box-rulesets'] });
      queryClient.invalidateQueries({ queryKey: ['packaging-box-ruleset-detail', data.id] });
    },
  });

  const currentDefaultBoxGroupId = boxRulesetDetailQuery.data?.default_box_group_id ?? null;

  const saveDefaultBoxGroup = async (groupId: number | null): Promise<void> => {
    if (activeBoxRulesetId) {
      await apiAdapter.updateBoxRuleset(activeBoxRulesetId, {
        active: true,
        default_box_group_id: groupId,
        fallback_box_group_id: boxRulesetDetailQuery.data?.fallback_box_group_id ?? null,
      });
      queryClient.invalidateQueries({ queryKey: ['packaging-box-ruleset-detail', activeBoxRulesetId] });
      queryClient.invalidateQueries({ queryKey: ['packaging-box-rulesets'] });
    } else {
      await createBoxRulesetMutation.mutateAsync({ active: true, default_box_group_id: groupId });
    }
  };

  const saveFallbackBoxGroup = async (groupId: number | null): Promise<void> => {
    if (!activeBoxRulesetId) return;
    await apiAdapter.updateBoxRuleset(activeBoxRulesetId, {
      active: true,
      default_box_group_id: currentDefaultBoxGroupId,
      fallback_box_group_id: groupId,
    });
    queryClient.invalidateQueries({ queryKey: ['packaging-box-ruleset-detail', activeBoxRulesetId] });
    queryClient.invalidateQueries({ queryKey: ['packaging-box-rulesets'] });
  };

  const allBoxGroups = useMemo(() => {
    const childScoped = allBoxGroupsQuery.data ?? [];
    if (!isScopedChildTeam) return childScoped;
    const parentWarehouse = parentWarehouseBoxGroupsQuery.data ?? [];
    const seen = new Set<number>();
    const merged: typeof childScoped = [];
    for (const g of childScoped) {
      if (!seen.has(g.id)) {
        seen.add(g.id);
        merged.push(g);
      }
    }
    for (const g of parentWarehouse) {
      if (!seen.has(g.id)) {
        seen.add(g.id);
        merged.push(g);
      }
    }
    return merged;
  }, [isScopedChildTeam, allBoxGroupsQuery.data, parentWarehouseBoxGroupsQuery.data]);

  return {
    boxRulesets: boxRulesetsQuery.data ?? [],
    marketingRulesets: marketingRulesetsQuery.data ?? [],
    countries: countriesQuery.data ?? [],
    allBoxGroups,
    isBoxGroupsLoading: allBoxGroupsQuery.isLoading || (isScopedChildTeam && parentWarehouseBoxGroupsQuery.isLoading),
    marketingCatalogItems: marketingItemsQuery.data ?? [],
    activeBoxRuleset: boxRulesetDetailQuery.data ?? null,
    boxExceptions: boxExceptionsQuery.data ?? [],
    isLoading:
      boxRulesetsQuery.isLoading ||
      marketingRulesetsQuery.isLoading ||
      allBoxGroupsQuery.isLoading ||
      (isScopedChildTeam && parentWarehouseBoxGroupsQuery.isLoading) ||
      boxRulesetDetailQuery.isLoading,
    isBoxExceptionsLoading:
      boxRulesetsQuery.isLoading || boxRulesetDetailQuery.isLoading || boxExceptionsQuery.isLoading,
    isMarketingCatalogLoading: marketingItemsQuery.isLoading,
    boxRulesetId: activeBoxRulesetId,
    marketingRulesetId: marketingRulesetsQuery.data?.[0]?.id ?? null,
    createException: createExceptionMutation.mutateAsync,
    isCreatingException: createExceptionMutation.isPending,
    updateBoxException: updateBoxExceptionMutation.mutateAsync,
    deleteBoxException: deleteBoxExceptionMutation.mutateAsync,
    createMarketingRule: createMarketingRuleMutation.mutateAsync,
    isCreatingMarketingRule: createMarketingRuleMutation.isPending,
    updateMarketingRule: updateMarketingRuleMutation.mutateAsync,
    deleteMarketingRule: deleteMarketingRuleMutation.mutateAsync,
    createMarketingRuleset: createMarketingRulesetMutation.mutateAsync,
    isCreatingMarketingRuleset: createMarketingRulesetMutation.isPending,
    saveDefaultBoxGroup,
    saveFallbackBoxGroup,
    isUpdatingBoxRuleset: updateBoxRulesetMutation.isPending || createBoxRulesetMutation.isPending,
    isSyncingBoxRuleset:
      updateBoxRulesetMutation.isPending ||
      createBoxRulesetMutation.isPending ||
      boxRulesetsQuery.isFetching ||
      boxRulesetDetailQuery.isFetching,
  };
};
